import { useGetInstitutions } from '@src/api/useInstitutions';
import { ApiInstitution } from '@repo/types';
import { useDebounce } from '@src/hooks/useDebounce';
import { FormAutocompleteSelectField } from './FormAutocompleteSelectField';

interface InstitutionSelectorProps {
  label: string;
  id: string;
  error?: string | null;
  fieldClassName?: string;
  labelClassName?: string;
  required?: boolean;
  value: string;
  onChange: (value: string) => void;
  onInstitutionSelect?: (institution: ApiInstitution | null) => void;
  placeholder?: string;
  disabled?: boolean;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}

export function InstitutionSelector({
  label,
  id,
  error,
  fieldClassName,
  labelClassName,
  required,
  value,
  onChange,
  onInstitutionSelect,
  placeholder = 'Select institution',
  disabled = false,
  onKeyDown,
}: InstitutionSelectorProps) {
  const debouncedValue = useDebounce(value, 500);
  const { data: institutions, isLoading } = useGetInstitutions({
    types: ['education'],
    status: 'active',
    term: debouncedValue,
  });
  const isSearching = isLoading || value !== debouncedValue;

  const handleChange = (newValue: string) => {
    onChange(newValue);
    onInstitutionSelect?.(null);
  };

  const handleInstitutionSelect = (institution: ApiInstitution) => {
    onChange(institution.name);
    onInstitutionSelect?.(institution);
  };

  const options = (institutions ?? []).map((institution) => ({
    key: institution.id,
    label: institution.name,
    onSelect: () => handleInstitutionSelect(institution),
  }));

  return (
    <FormAutocompleteSelectField
      label={label}
      id={id}
      error={error}
      fieldClassName={fieldClassName}
      labelClassName={labelClassName}
      required={required}
      onChange={handleChange}
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      onKeyDown={onKeyDown}
      options={options}
      isLoading={isSearching}
    />
  );
}
